import { Ionicons } from '@expo/vector-icons';
import { usePathname, useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { fontSize, radius, spacing } from '@/constants/theme';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { fetchProfilesByIds } from '@/lib/agreements';
import { DELETED_SENDER_ID, type DbAgreementMessage } from '@/lib/agreementMessages';
import { playPop } from '@/lib/sounds';

const AQUA = '#0BC5C0';

type Toast = {
  agreementId: string;
  senderName: string;
  body: string;
};

/** Παγκόσμια ειδοποίηση σε στυλ bubble όταν φτάνει νέο chat μήνυμα σε
 * οποιαδήποτε συμφωνία — ανεξάρτητα από την τρέχουσα οθόνη. Tap πάει
 * κατευθείαν στο chat της συμφωνίας. Δεν εμφανίζεται αν ο χρήστης είναι ήδη
 * μέσα σε αυτό το ακριβές chat. */
export function IncomingMessageToast() {
  const { user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const [toast, setToast] = useState<Toast | null>(null);
  const anim = useRef(new Animated.Value(0)).current;
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('global-agreement-messages')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'agreement_messages',
      }, (payload) => {
        void (async () => {
          const raw = payload.new as DbAgreementMessage;
          const row = { ...raw, sender_id: raw.sender_id ?? DELETED_SENDER_ID };
          if (row.sender_id === user.id) return;
          // Ήδη μέσα στο ίδιο ακριβώς chat — δεν χρειάζεται toast.
          if (pathnameRef.current === `/agreements/messages/${row.agreement_id}`) return;
          const senders = await fetchProfilesByIds([row.sender_id]).catch(() => ({} as Record<string, { full_name: string | null }>));
          const senderName = senders[row.sender_id]?.full_name || t('common.user');
          setToast({ agreementId: row.agreement_id, senderName, body: row.body });
          playPop();
        })();
      })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user, t]);

  useEffect(() => {
    if (!toast) return;
    Animated.spring(anim, { toValue: 1, useNativeDriver: Platform.OS !== 'web', bounciness: 8, speed: 14 }).start();
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => dismiss(), 5000);
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  function dismiss() {
    Animated.timing(anim, { toValue: 0, duration: 180, useNativeDriver: Platform.OS !== 'web' }).start(() => setToast(null));
  }

  function onPress() {
    if (!toast) return;
    const agreementId = toast.agreementId;
    dismiss();
    router.push({ pathname: '/(app)/agreements/messages/[id]', params: { id: agreementId } } as any);
  }

  const styles = useMemo(() => makeStyles(), []);

  if (!toast) return null;

  return (
    <Animated.View
      pointerEvents="box-none"
      style={[
        styles.container,
        {
          top: insets.top + spacing.sm,
          opacity: anim,
          transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [-40, 0] }) }],
        },
      ]}
    >
      <Pressable style={styles.bubble} onPress={onPress} accessibilityRole="button">
        <View style={styles.iconCircle}>
          <Ionicons name="chatbubble-ellipses" size={18} color="#fff" />
        </View>
        <View style={styles.textBlock}>
          <Text style={styles.name} numberOfLines={1}>{toast.senderName}</Text>
          <Text style={styles.body} numberOfLines={1}>{toast.body}</Text>
        </View>
        <Pressable onPress={dismiss} hitSlop={10} accessibilityRole="button">
          <Ionicons name="close" size={16} color="rgba(255,255,255,0.85)" />
        </Pressable>
      </Pressable>
    </Animated.View>
  );
}

function makeStyles() {
  return StyleSheet.create({
    container: {
      position: 'absolute',
      left: spacing.md,
      right: spacing.md,
      zIndex: 999,
    },
    bubble: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: AQUA,
      borderRadius: radius.lg,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.25,
      shadowRadius: 8,
      elevation: 8,
    },
    iconCircle: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: 'rgba(255,255,255,0.25)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    textBlock: { flex: 1, gap: 1 },
    name: { color: '#fff', fontSize: fontSize.sm, fontWeight: '800' },
    body: { color: 'rgba(255,255,255,0.9)', fontSize: fontSize.xs },
  });
}
